import { useComputed } from "@suse-o11y/signal";
import sortedUniq from "lodash/sortedUniq";
import React from "react";

import { IconChevronDown } from "~/components/icon";
import { alphabeticalSorter } from "~/tools/alphabetical-sorter";
import { cx } from "~/tools/class-variance";
import type { JSON } from "~/tools/json";
import { sort } from "~/tools/sort";
import { toJSON } from "~/tools/to-json";

import { useToggleListLocation } from "./_toggle-list-location-context";
import { useToggleListNamespace } from "./_toggle-list-root-context";
import { useToggleListState } from "./_toggle-list-state-context";

const ToggleListButton: React.FC<{
	count: number;
}> = ({ count }) => {
	const state = useToggleListState();
	const namespace = useComputed(useToggleListNamespace());
	const isExpanded = useComputed((monitor) => state.isExpanded(monitor, namespace));

	// collapse automatically if children become less than 2
	React.useEffect(() => {
		if (count < 2) {
			state.expand(namespace, false);
		}
	}, [state, count, namespace]);

	return (
		<button
			aria-checked={isExpanded}
			className="-ml-1.5 flex cursor-pointer items-center rounded-xs border-none bg-transparent p-0 pr-1.5 leading-[21px] underline outline-hidden transition-colors hover:no-underline"
			data-testid="toggle-list-button"
			onClick={() => state.expand(namespace, !isExpanded)}
			type="button"
		>
			<IconChevronDown className={cx("transition-transform", !isExpanded && "-rotate-90")} height={16} width={20} />
			{count}
		</button>
	);
};

export interface ToggleListProps {
	namespace?: JSON;
	each: ReadonlyArray<string>;
	empty?: React.ReactNode;
	children: (item: string, index: number) => null | React.ReactNode;
}

export const ToggleList: React.FC<ToggleListProps> = ({ namespace: namespaceFromProps, each, empty, children }) => {
	const namespace = useToggleListNamespace();
	const location = useToggleListLocation();
	const stringifiedNamespace = namespaceFromProps == null ? "" : toJSON(namespaceFromProps);

	const sortedUniqItems = React.useMemo(() => sortedUniq(sort(each, alphabeticalSorter)), [each]);

	React.useEffect(() => {
		namespace.write(stringifiedNamespace);
	}, [namespace, stringifiedNamespace]);

	if (location === "trigger" && sortedUniqItems.length > 1) {
		return <ToggleListButton count={sortedUniqItems.length} />;
	}

	if (sortedUniqItems.length === 0) {
		return <>{empty}</>;
	}

	return (
		<>
			{sortedUniqItems.map((item, index) => (
				<React.Fragment key={item}>{children(item, index)}</React.Fragment>
			))}
		</>
	);
};
